/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import sortRacesWithSAPriority from "../../../utils/raceCourseUtils";
import authorizedAxios from "../../apiJwtClient";

/**
 * Leaderboard Interfaces
 */
export interface IUpcomingRaceMeetings {
  meetingId: number;
  meetingDate: number;
  meetingStatusId: number;
  raceCourseName: string;
  raceTrackAssetId: number;
  jackpotAmount?: number;
}

export interface IGetDailyLeaderboardParameters {
  pageId: number;
  meetingId: number;
  numberPerPage: number;
  searchFor: string;
}

export interface ILeaderboardSelections {
  horseName: string;
  points: number;
  raceNumber: number;
  saddle: number;
  racePosition: number;
  raceStatus: number;
}

export interface IDailyLeaderboardData {
  userId: string;
  punterId: number;
  date: string;
  places: number;
  racePoints: number;
  rank: number;
  previousRank: number;
  winners: number;
  secondPlace: number;
  thirdPlace: number;
  selections: number;
  selectedHorses: ILeaderboardSelections[];
  total: number;
  totalPunters: number;
  totalSelections: number;
}

export interface IDailyLeaderboard {
  punters: IDailyLeaderboardData[];
  totalPunters: number;
}

export interface ITodaysRacesParameters {
  selectedDate: string;
}

export interface ITodaysRaces {
  meetingName: string;
  meetingDate: string;
  meetingId: number;
  punterPoints: number;
  punterRank: number;
  meetingStatusId: number;
}

export interface IPunterLeaderboardSelectionsParameters {
  meetingId: number;
  punterId: number;
}

interface ILeaderboardSliceNew {
  upcomingRaceMeetings: IUpcomingRaceMeetings[];
  dailyLeaderboardData: IDailyLeaderboard;
  getTodaysRaces: ITodaysRaces[];
  currentMeetingId: number;
  pageNumber: number;
  selections: Record<string, ILeaderboardSelections[]>;
  totalPages: number;
  LeaderboardDate: string;
}

/**
 * Config Data
 */
const configConst = (state: RootState) => state.config.data;

/**
 * Todays Races which accepts a date string in the parameter
 */

export const GetMeetingsForSelectedDateAsync: any = createAsyncThunk(
  "GetMeetingsForSelectedDateAsync",
  async (
    { selectedDate }: ITodaysRacesParameters,
    { getState }
  ): Promise<ITodaysRaces> => {
    const config = configConst(getState() as RootState);
    const data = await authorizedAxios.get(`
      ${config.puntersApi}/api/Leaderboard/GetMeetingsForSelectedDate/${selectedDate}
    `);

    return data.data.meetingDetails;
  }
);

/**
 * Returns the leaders ,20 in number, users rank is added at the end.
 */
export const GetDailyLeaderboardData: any = createAsyncThunk(
  "GetDailyLeaderboardData",
  async (
    {
      pageId,
      meetingId,
      numberPerPage,
      searchFor
    }: IGetDailyLeaderboardParameters,
    { getState }
  ): Promise<{
    data: IDailyLeaderboardData[];
    parameters: IGetDailyLeaderboardParameters;
  }> => {
    const config = configConst(getState() as RootState);

    const data = await authorizedAxios.get(
      `${
        config.puntersApi
      }/api/Leaderboard/GetDailyLeaderboardPaged/${pageId}/${numberPerPage}/${meetingId}?username=${
        searchFor || ""
      }`
    );

    return {
      data: data.data.dailyLeaderboard,
      parameters: { pageId, meetingId, numberPerPage, searchFor }
    };
  }
);

/**
 * Returns the selected punters selection.
 * */
export const GetLeaderboardPuntersSelections: any = createAsyncThunk(
  "GetLeaderboardPuntersSelections",
  async (
    { punterId, meetingId }: IPunterLeaderboardSelectionsParameters,
    { getState }
  ): Promise<{ punterId: number; data: ILeaderboardSelections[] }> => {
    const config = configConst(getState() as RootState);
    const selectionState = getState() as RootState;

    if (!selectionState.leaderboardNew.selections[punterId]) {
      const data = await authorizedAxios.get(
        `${config.puntersApi}/api/Result/GetPunterLeaderboardSelections?meetingId=${meetingId}&punterId=${punterId}`
      );
      return { punterId, data: data.data.punterLeaderboardDetails };
    }
    return {
      punterId,
      data: selectionState.leaderboardNew.selections[punterId]
    };
  }
);

/**
 * Upcoming Race Information
 */
export const getUpcomingMeetingsForLeaderboard: any = createAsyncThunk(
  "getUpcomingMeetingsForLeaderboard",
  async (_, { getState }): Promise<IUpcomingRaceMeetings[]> => {
    const config = configConst(getState() as RootState);

    const upcomingRaces = await authorizedAxios.get(
      `${config.puntersApi}/api/Meeting/GetUpcomingRaceMeetings`
    );

    return upcomingRaces.data.upcomingMeeting;
  }
);

const initialState: ILeaderboardSliceNew = {
  upcomingRaceMeetings: [],
  dailyLeaderboardData: { punters: [], totalPunters: 0 },
  getTodaysRaces: [],
  currentMeetingId: 0,
  pageNumber: 0,
  selections: {},
  totalPages: 0,
  LeaderboardDate: ""
};

const LeaderboardSliceNew = createSlice({
  name: "LeaderboardSliceNew",
  initialState,
  reducers: {
    setCurrentMeetingId: (state, action: PayloadAction<number>) => {
      state.currentMeetingId = action.payload;
    },
    setPageNumber: (state, action: PayloadAction<number>) => {
      state.pageNumber = action.payload;
    },
    setClearTodaysRaces: (state) => {
      state.getTodaysRaces = [];
    },
    setClearLeaderboardData: (state) => {
      state.dailyLeaderboardData.punters = [];
    },
    setLeaderboardDate: (state, action: PayloadAction<string>) => {
      state.LeaderboardDate = action.payload;
    },
    setClearLeaderboardDate: (state) => {
      state.LeaderboardDate = "";
    },
    setClearSelections: (state) => {
      state.selections = {};
    }
  },
  extraReducers: (builder) => {
    /**
     * getUpcomingRaceMeetingsForLeaderboard Builders
     */
    builder.addCase(
      getUpcomingMeetingsForLeaderboard.fulfilled,
      (state, action: PayloadAction<IUpcomingRaceMeetings[]>) => {
        state.upcomingRaceMeetings = sortRacesWithSAPriority(
          action.payload
        ) as IUpcomingRaceMeetings[];
      }
    );

    /**
     * GetDailyLeaderboardData Builders
     */
    builder.addCase(
      GetDailyLeaderboardData.fulfilled,
      (
        state,
        action: PayloadAction<{
          data: IDailyLeaderboard;
          parameters: IGetDailyLeaderboardParameters;
        }>
      ) => {
        LeaderboardSliceNew.caseReducers.setClearLeaderboardData(state);
        LeaderboardSliceNew.caseReducers.setClearSelections(state);

        const { data, parameters } = action.payload;

        state.dailyLeaderboardData = {
          punters: data.punters,
          totalPunters: data.totalPunters
        };

        state.pageNumber = parameters.pageId;

        state.totalPages = Math.ceil(data.totalPunters / 20);
      }
    );

    /**
     * Todays Races Builder
     */
    builder.addCase(
      GetMeetingsForSelectedDateAsync.fulfilled,
      (state, action: PayloadAction<ITodaysRaces[]>) => {
        LeaderboardSliceNew.caseReducers.setClearLeaderboardData(state);
        LeaderboardSliceNew.caseReducers.setClearTodaysRaces(state);
        state.getTodaysRaces = action.payload.toSorted((a, b) => {
          if (a.meetingStatusId === 4 && b.meetingStatusId !== 4) return 1;
          if (a.meetingStatusId !== 4 && b.meetingStatusId === 4) return -1;

          const nonSARaces = ["Sha Tin", "Happy Valley", "Kranji", "Meydan"];
          const aIsNonSA = nonSARaces.includes(a.meetingName);
          const bIsNonSA = nonSARaces.includes(b.meetingName);

          if (!aIsNonSA && bIsNonSA) return -1;
          if (aIsNonSA && !bIsNonSA) return 1;

          return 0;
        });
      }
    );
    /**
     * Punter Selections Builder
     */
    builder.addCase(
      GetLeaderboardPuntersSelections.fulfilled,
      (
        state,
        action: PayloadAction<{
          punterId: number;
          data: ILeaderboardSelections[];
        }>
      ) => {
        state.selections[action.payload.punterId] = action.payload.data;
      }
    );
  }
});

export const {
  setCurrentMeetingId,
  setClearTodaysRaces,
  setClearLeaderboardData,
  setLeaderboardDate,
  setClearLeaderboardDate
} = LeaderboardSliceNew.actions;

export default LeaderboardSliceNew.reducer;
